import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Flex, Box } from 'reflexbox/styled-components'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { kebabCase } from 'lodash'
import { toPlainText } from '@portabletext/react'

import { H6 } from 'lib/ui/primitives'

import { BLOG_TAGS } from '@anvilco-pkg/sanity'

import VideoModal from 'components/VideoModal'
import UniversalLink from 'components/UniversalLink'

import theme from 'theme'
import media from 'media'
import { BLOG_SLUG, HELP_SLUG, FEATURE_SLUG } from 'consts'
import useMetadata from 'hooks/useMetadata'
import { getProductSlug } from 'helpers/path'

import placeholderWebp from './assets/placeholder.webp'

const ResourceCard = styled(Flex).attrs({
  flexDirection: ['column', 'column', 'row'],
})`
  gap: 20px;
`

const ResourceImage = styled(GatsbyImage)`
  width: 100%;
  height: auto;
  flex-shrink: 0;

  ${media.desktop`
    width: 168px;
    height: 95px;
  `}
`

const ProductImage = styled.img`
  width: 100%;
  height: auto;
  flex-shrink: 0;

  ${media.desktop`
    width: 168px;
    height: 95px;
  `}
`

const ResourceTitle = styled(H6).attrs({
  as: 'h3',
  fontSize: [18],
  mb: '2px',
})``

const ResourceTag = styled(Box).attrs({
  fontSize: 12,
  lineHeight: 1.5,
  color: theme.colors.blacks[40],
  mb: '4px',
})`
  text-transform: uppercase;
`

const ResourceDetail = styled(Flex).attrs({
  flexDirection: 'column',
  justifyContent: 'center',
})``

const getTagTitle = (tag) => {
  return BLOG_TAGS.find((t) => t.key === tag)?.value ?? tag
}

const getResourceData = (resource, markdowns, covers) => {
  if (resource.__typename === 'SanityFeature') {
    const tag = getProductSlug(resource.primaryProduct || resource.product[0])
    return {
      tag: 'Feature',
      title: resource.name,
      imageData: resource.coverImage.asset.gatsbyImageData,
      description: resource.description,
      to: `/products/${kebabCase(tag)}/${FEATURE_SLUG}/${kebabCase(resource.name)}/`,
    }
  }
  if (resource.__typename === 'SanityBlogPost') {
    const tag = resource.article.primaryTag || resource.article.tags[0]
    return {
      tag: getTagTitle(tag),
      title: resource.article.title,
      imageData: resource.heroImage.asset.gatsbyImageData,
      to: `/${BLOG_SLUG}/${kebabCase(tag)}/${resource.article.slug}`,
      description: toPlainText(resource.article._rawSummary),
    }
  }
  if (resource.__typename === 'SanityYoutubeVideo') {
    return {
      tag: 'Video',
      title: resource.title,
      description: resource.description,
      imageData: `https://img.youtube.com/vi/${resource.videoId}/hqdefault.jpg`,
      videoId: resource.videoId,
    }
  }
  if (resource.path.includes(BLOG_SLUG) || resource.path.includes(HELP_SLUG)) {
    const slug = resource.path.split('/').at(-2)
    const markdown = markdowns.find((md) => md.frontmatter.slug === slug)
    if (markdown) {
      const title = markdown?.frontmatter?.title ?? ''
      const description = markdown?.frontmatter?.summary ?? ''
      const cover = covers.find((cover) => cover.title === title)
      return {
        title,
        imageData: cover?.gatsbyImageData,
        tag: getTagTitle(markdown?.frontmatter?.tags?.[0] ?? ''),
        to: resource.path,
        description,
      }
    }
  }
  return null
}

const ShowVideoLink = styled(Box)`
  color: ${({ theme: currentTheme }) =>
    currentTheme?.textColors?.link || theme.colors.black};
  cursor: pointer;
  text-decoration: underline;
  font-weight: ${theme.fontWeights.bold};
  transition: all 0.2s;

  &:hover {
    color: ${({ theme: currentTheme }) =>
      currentTheme?.textColors?.linkHover || theme.textColors.linkHover};
  }
`

const RelatedResource = ({ resource, markdowns, markdownCovers }) => {
  if (!resource) return null
  const data = getResourceData(resource, markdowns, markdownCovers)
  const metadata = useMetadata(resource.path, {
    byPassMetadata:
      resource.path?.includes(BLOG_SLUG) || resource.path?.includes(HELP_SLUG),
  })

  if (!data && !metadata) return null

  const resourceData = data || metadata
  const { imageData, title, tag, to, videoId, description } = resourceData

  const renderImage = () => {
    if (!imageData) return <ProductImage src={placeholderWebp} alt={title} />
    if (typeof imageData === 'string')
      return <ProductImage src={imageData} alt={title} />
    return <ResourceImage image={getImage(imageData)} />
  }

  return (
    <ResourceCard>
      {renderImage()}
      <ResourceDetail>
        <ResourceTag>{tag}</ResourceTag>
        <ResourceTitle>{title}</ResourceTitle>
        {videoId ? (
          <VideoModal
            title={title}
            description={description}
            videoId={videoId}
            renderPlaceholder={({ onClick }) => {
              return <ShowVideoLink onClick={onClick}>Learn more</ShowVideoLink>
            }}
          />
        ) : (
          <UniversalLink to={to}>Learn more</UniversalLink>
        )}
      </ResourceDetail>
    </ResourceCard>
  )
}

RelatedResource.propTypes = {
  resource: PropTypes.object,
  markdowns: PropTypes.arrayOf(PropTypes.object),
  markdownCovers: PropTypes.arrayOf(PropTypes.object),
  seoData: PropTypes.arrayOf(PropTypes.object),
}

export default RelatedResource
